<template>
  <div class="order-summary">
    <SummaryBackground />
    <SummaryHeader :subTitle="headerSubTitle" />
    <SummaryStatus :summaryStatusData="statusData" />
    <SummaryTable :summaryData="orderData" :isLoading="isOrderLoading" />
    <ButtonBase
      @onButtonClick="handleButtonClick"
      :isDisabled="isOrderLoading"
      data-cy="button-return"
      v-if="shouldShowButton"
    >
      {{ returnButtonLabel }}
    </ButtonBase>
    <p class="term-use">
      {{ t('SummaryTable.additionalInfo') }}
    </p>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  computed,
  onBeforeMount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTitle } from '@vueuse/core';
import { camelize } from '@/composables/helpers/useStringHelpers.js';
import useApiHelpers from '@/composables/api/helpers';

import { useGlobalStore } from '@/stores/globalStore';

import SummaryBackground from '@/components/layout/summary/SummaryBackground.vue';
import SummaryHeader from '@/components/layout/summary/SummaryHeader.vue';
import SummaryStatus from '@/components/layout/summary/SummaryStatus.vue';
import SummaryTable from '@/components/layout/summary/SummaryTable.vue';
import ButtonBase from '@/components/ui/buttons/ButtonBase.vue';
import useModalActions from '@/composables/store/modal/useModalActions';
import { useIdentityQueueStore } from '@/stores/identityQueueStore';

const store = useGlobalStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const shouldShowButton = computed(
  () => !store.global?.merchant?.hideReturnButton ?? !store.getIsGreyLabel,
);

const statusData = ref({
  // defaults
  payment: {
    label: 'Pending',
    title: 'Payment',
    color: '#e9ae6a',
    textstatus: 'pending',
    icon: 'StatusPending',
  },
  identity: {
    label: 'Pending',
    title: 'Identity',
    color: '#e9ae6a',
    textstatus: 'pending',
    icon: 'StatusPending',
  },
  fulfillment: {
    label: 'Pending',
    title: 'Fulfilment',
    color: '#e9ae6a',
    textstatus: 'pending',
    icon: 'StatusPending',
  },
});

const mapStatusConfig = [
  {
    status: 'complete', // cl_core is not "completed"
    color: '#03c5b3',
    icon: 'StatusCompleted',
  },
  {
    status: 'pending',
    color: '#e9ae6a',
    icon: 'StatusPending',
  },
  {
    status: 'actionRequired',
    color: '#e9ae6a',
    icon: 'StatusPending',
  },
  {
    status: 'underReview',
    color: '#e9ae6a',
    icon: 'StatusPending',
  },
  {
    status: 'preparingOrder',
    color: '#e9ae6a',
    icon: 'StatusPending',
  },
  {
    status: 'processing',
    color: '#e9ae6a',
    icon: 'StatusPending',
  },
  {
    status: 'failed',
    color: '#EC5F59',
    icon: 'StatusFailed',
  },
  {
    status: 'expired',
    color: '#EC5F59',
    icon: 'StatusFailed',
  },
  {
    status: 'refunded',
    color: '#e9ae6a',
    icon: 'StatusPending',
  },
];

const statusesToRepollFor = [
  'pending',
  'action required',
  'under review',
  'preparing order',
  'processing',
];
const orderCancelledStatus = ['EXPIRED', 'CANCELED'];
const orderFailedStatus = ['DECLINED', 'FRAUD', 'REFUNDED'];

const orderData = ref({});
const returnButtonLabel = ref('');
const merchantReturnURL = ref({
  successURL: '',
  cancelURL: '',
  failedURL: '',
});
const isOrderLoading = ref(false);
const headerSubTitle = ref('');
const maxRepollAttempts = 6;
let attempts = 0;
let response;
let checkInterval;

const redirectToUrl = (url) => {
  if (store.getIsInIFrame) {
    window.parent.location = url;
    return;
  }
  window.location = url;
};

const { getRequest } = useApiHelpers();
const { toggleFormComponentModal } = useModalActions();
const handleButtonClick = () => {
  if (store.global.merchant.returnUrlIsFromQuery) {
    redirectToUrl(store.global.merchant.returnUrl);
    return;
  }

  if (
    merchantReturnURL.value.cancelURL !== ''
    && orderCancelledStatus.includes(orderData.value?.status?.toUpperCase())
  ) {
    redirectToUrl(merchantReturnURL.value.cancelURL);
    return;
  }

  if (
    merchantReturnURL.value.failedURL !== ''
    && orderFailedStatus.includes(orderData.value?.status?.toUpperCase())
  ) {
    redirectToUrl(merchantReturnURL.value.failedURL);
    return;
  }

  if (merchantReturnURL.value.successURL !== '') {
    redirectToUrl(merchantReturnURL.value.successURL);
    return;
  }

  router.push('/');
};

const updateStatusData = (apiData) => {
  Object.entries(apiData).forEach(([status, values]) => {
    if (values.label) {
      const config = mapStatusConfig.find(
        (configStatus) => configStatus.status === camelize(values.label),
      );
      statusData.value[status] = { ...config, ...values };
    }

    if (statusData.value[status]?.label === 'Complete') {
      statusData.value[status].label = 'Completed';
    }
  });
};

const loadStatus = (orderResponse) => {
  const { status, summary, partnerReturnUrl } = orderResponse.data.data;
  updateStatusData(status);
  headerSubTitle.value = orderStatusBodyMessage(status);
  orderData.value = summary;

  if (store.global.merchant.returnUrlIsFromQuery) {
    returnButtonLabel.value = store.global.merchant.returnMessage;
  } else if (
    partnerReturnUrl?.data?.message
    && partnerReturnUrl?.data?.message !== ''
  ) {
    returnButtonLabel.value = partnerReturnUrl.data.message;
    merchantReturnURL.value.successURL = partnerReturnUrl.data.return_url_on_success;
    merchantReturnURL.value.cancelURL = partnerReturnUrl.data.return_url_on_cancelled;
    merchantReturnURL.value.failedURL = partnerReturnUrl.data.return_url_on_failure;
  } else {
    returnButtonLabel.value = 'Start a new order';
  }

  isOrderLoading.value = false;
};

const checkIfStatusRequiresRepoll = (responseData) => Object.values(responseData).some((statusKeys) => {
  const status = statusKeys.hasOwnProperty('label')
      ? statusKeys.label.toLowerCase()
      : '';
  return statusesToRepollFor.includes(status);
});

const redirectToMerchant = (orderResponse) => {
  const { summary, partnerReturnUrl } = orderResponse.data.data;
  orderData.value = summary;
  const includesOrderFailedStatus = orderFailedStatus.includes(
    orderData.value?.status?.toUpperCase(),
  );
  const includesOrderCancelledStatus = orderCancelledStatus.includes(
    orderData.value?.status?.toUpperCase(),
  );

  if (
    partnerReturnUrl.data.return_url_on_cancelled !== ''
    && includesOrderCancelledStatus
  ) {
    redirectToUrl(partnerReturnUrl.data.return_url_on_cancelled);
    return;
  }

  if (
    partnerReturnUrl.data.return_url_on_failure !== ''
    && includesOrderFailedStatus
  ) {
    redirectToUrl(partnerReturnUrl.data.return_url_on_failure);
    return;
  }

  if (partnerReturnUrl.data.return_url_on_success !== '') {
    redirectToUrl(partnerReturnUrl.data.return_url_on_success);
  }
};

const checkIfStatusRequiresCcSelfie = (actions) => actions
    && Array.isArray(actions)
    && actions.includes('CC_SELFIE');

/** actions includes DOCUMENT, SELFIE, PERSONAL_DETAILS, ..., but could also be CC_SELFIE or potentially other actions.
 * We redirect to the KYC queue for everything but CC_SELFIE (which is handled by a modal on this page)
 */
const checkIfStatusRequiresKycRecollection = (actions) => actions
    && Array.isArray(actions)
    && actions.length
    && !actions.includes('CC_SELFIE');

const orderStatusBodyMessage = (orderStatus) => {
  const ORDER_STATUS_COMPLETE = 'Complete';
  const paymentStatus = orderStatus.payment?.label;
  const identityStatus = orderStatus.identity?.label;
  const fulfillmentStatus = orderStatus.fulfillment?.label;

  // Payment and identity are pending
  if (
    paymentStatus !== ORDER_STATUS_COMPLETE
    && identityStatus !== ORDER_STATUS_COMPLETE
  ) {
    return t('OrderSummaryView.label.paymentAndIdentityPending');
  }

  // Payment is pending
  if (
    paymentStatus !== ORDER_STATUS_COMPLETE
    && identityStatus === ORDER_STATUS_COMPLETE
  ) {
    return t('OrderSummaryView.label.paymentPending');
  }

  // Identity is pending
  if (
    identityStatus !== ORDER_STATUS_COMPLETE
    && paymentStatus === ORDER_STATUS_COMPLETE
  ) {
    return t('OrderSummaryView.label.identityPending');
  }

  // Payment and Identity are complete, fulfillment is pending
  if (
    paymentStatus === ORDER_STATUS_COMPLETE
    && identityStatus === ORDER_STATUS_COMPLETE
    && fulfillmentStatus !== ORDER_STATUS_COMPLETE
  ) {
    return t('OrderSummaryView.label.fullfilmentPending');
  }

  // Payment, Identity, and Fulfillment are complete
  if (
    paymentStatus === ORDER_STATUS_COMPLETE
    && identityStatus === ORDER_STATUS_COMPLETE
    && fulfillmentStatus === ORDER_STATUS_COMPLETE
  ) {
    return t('OrderSummaryView.label.fulfillmentCompleted');
  }

  return t('OrderSummaryView.label.defaultSubtitle');
};

const handleCCSelfieFormAction = async () => {
  toggleFormComponentModal(false);
  response = await getRequest(`/status/${route.params.orderId}`);
  if (store.getIsAutoRedirectToMerchant) {
    redirectToMerchant(response);
  }
  loadStatus(response);
};

/**
 * Check the history state to see if the user is coming from the payment history page
 */
const checkPaymentHistoryState = () => {
  const { state } = window.history;
  return Boolean(state.back) || Boolean(state?.isRedirected);
};

const removePaymentCompleteFlag = () => {
  sessionStorage.removeItem('payActionDone');
};

onBeforeMount(async () => {
  removePaymentCompleteFlag();
  response = await getRequest(`/status/${route?.params?.orderId}`);
  const { actions } = response.data.data;

  if (
    store.getIsAutoRedirectToMerchant
    && !checkIfStatusRequiresCcSelfie(actions)
    && !checkIfStatusRequiresKycRecollection(actions)
    && checkPaymentHistoryState()
  ) {
    redirectToMerchant(response);
  }
});

onMounted(async () => {
  useTitle('Banxa - Hooray!');
  isOrderLoading.value = true;
    if (!response) {
    response = await getRequest(`/status/${route?.params?.orderId}`);
  }
    const { status, actions } = response.data.data;
  const identityQueueStore = useIdentityQueueStore();

  if (checkIfStatusRequiresCcSelfie(actions)) {
    const isMobile = window.innerWidth <= 768;
    const alignHeight = isMobile ? '855px' : '1000px';

    toggleFormComponentModal(
      true,
      'Identity/CcSelfieForm',
      {
          primaryBtnAction: handleCCSelfieFormAction,
          secondaryBtnAction: null,
      },
      {
        overflow: 'auto',
        'min-height': alignHeight,
        'justify-content': 'center',
        },
      );
  }

  if (checkIfStatusRequiresKycRecollection(actions)) {
    identityQueueStore.toggleSumsubRecollection(true);
    await identityQueueStore.fetchIdentityDocuments(route.path).then(() => {
      const newRoute = identityQueueStore.identityRoutes[identityQueueStore.queueCounter - 1]
        .url;
      router.push(newRoute);
    });
    return;
  }

  if (checkIfStatusRequiresRepoll(status)) {
    checkInterval = setInterval(async () => {
      attempts += 1;
      response = await getRequest(`/status/${route.params.orderId}`);
      const orderStatuses = response.data.data;

      const requiresRepoll = checkIfStatusRequiresRepoll(orderStatuses?.status);

      if (attempts >= maxRepollAttempts || !requiresRepoll) {
        clearTimeout(checkInterval);
      }

      loadStatus(response);
      isOrderLoading.value = false;
    }, 2500);
  } else {
    clearTimeout(checkInterval);
    loadStatus(response);
  }
});

useTitle('Banxa - Hooray!');
</script>

<style lang="scss">
.order-summary {
  width: 100%;
  max-width: 50rem;
  padding-top: 2em;
  @include breakpoint($max-m) {
    padding: 5em 10px 0 10px;
  }

  .skeleton,
  .summary-table {
    margin-bottom: 5em;

    @include breakpoint($max-m) {
      margin-bottom: 3em;
    }
  }
}
</style>
